import { useServicesQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import type { ServicesApiCopilotVirtualFileSummary } from '@/services/api/services-api';

/** Maximum allowed skill content size in bytes (1 MB), matching the backend limit. */
export const MAX_SKILL_CONTENT_LENGTH = 1_048_576;

/** Check content size in bytes (not UTF-16 code units) to match the backend's byte limit. */
export function isContentOverLimit(content: string): boolean {
  return new TextEncoder().encode(content).length > MAX_SKILL_CONTENT_LENGTH;
}

export interface Skill {
  /** Display name without the .md extension, e.g. "my-report" */
  name: string;
  /** File name with extension, e.g. "my-report.md" */
  fileName: string;
  /** Full path as stored in the backend, e.g. "skills/my-report.md" */
  path: string;
  createdAt: string;
  updatedAt: string;
}

/** Strip the .md extension for display purposes. */
export function stripMdExtension(name: string): string {
  return name.replace(/\.md$/, '');
}

/** Sanitize a skill name into a valid file path segment, stripping any .md extension. */
export function toFileName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\.md$/i, '')
    .replace(/\s+/g, '-')
    .replace(/[^\d._a-z-]/g, '');
}

function toSkill(file: ServicesApiCopilotVirtualFileSummary): Skill {
  const fileName = file.path.replace(/^skills\//, '');
  return {
    name: stripMdExtension(fileName),
    fileName,
    path: file.path,
    createdAt: file.created_at,
    updatedAt: file.updated_at,
  };
}

export function useSkills() {
  const { account } = useAuth();

  const filesQuery = useServicesQuery(
    'listCopilotFiles',
    { params: { accountId: account.id } },
    {
      select: (data) => data.files.filter((f) => f.path.startsWith('skills/')).map(toSkill),
    },
  );

  const customSkillsFeature = account.planFeatures[PlanFeature.CustomSkills];
  const isUnlimited = hasUnlimitedPlanFeatureAccess(account, PlanFeature.CustomSkills);
  const maxSkills: number = isUnlimited
    ? Infinity
    : customSkillsFeature?.enabled === true && typeof customSkillsFeature.limit === 'number'
    ? customSkillsFeature.limit
    : 3;

  return {
    skills: filesQuery.data ?? [],
    isLoading: filesQuery.isLoading,
    isError: filesQuery.isError,
    maxSkills,
  };
}
